import React, { useState, useEffect } from 'react';
import { API_URL } from '../../config/api';
import { ChevronLeft, ChevronRight, Plus, X, Save, Calendar, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';

const getAdminAuth = () => sessionStorage.getItem('adminPassword') || '';
const getAuthHeaders = () => ({ 'Authorization': getAdminAuth() });

const WorkerSchedule = () => {
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - d.getDay() + 1); // Monday
    return d.toISOString().split('T')[0];
  });
  const [workers, setWorkers] = useState([]);
  const [sites, setSites] = useState([]);
  const [schedule, setSchedule] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addingCell, setAddingCell] = useState(null); // {date, workerId}
  const [selectedSite, setSelectedSite] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  const weekDates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d.toISOString().split('T')[0];
  });
  const endDate = weekDates[6];

  useEffect(() => { loadData(); }, [weekStart]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [workersRes, sitesRes, schedRes] = await Promise.all([
        fetch(`${API_URL}/api/worker/admin/workers`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/admin/sites`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/worker/admin/schedule?start_date=${weekStart}&end_date=${endDate}`, { headers: getAuthHeaders() })
      ]);
      const [wData, sData, schData] = await Promise.all([workersRes.json(), sitesRes.json(), schedRes.json()]);
      setWorkers(wData.workers || []);
      setSites(sData.sites || []);
      // Organize schedule: { "date|workerId": [entries] }
      const sched = {};
      (schData.schedule || []).forEach(e => {
        const key = `${e.date}|${e.worker_id}`;
        if (!sched[key]) sched[key] = [];
        sched[key].push(e);
      });
      setSchedule(sched);
      setHasChanges(false);
    } catch {}
    setLoading(false);
  };

  const shiftWeek = (direction) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + (direction * 7));
    setWeekStart(d.toISOString().split('T')[0]);
  };

  const addToSchedule = (date, workerId) => {
    if (!selectedSite) return;
    const site = sites.find(s => s.id === selectedSite);
    if (!site) return;
    const key = `${date}|${workerId}`;
    const existing = schedule[key] || [];
    const order = existing.length;
    const entry = {
      id: `new-${Date.now()}-${Math.random()}`,
      worker_id: workerId,
      site_id: selectedSite,
      site_name: site.site_name,
      site_address: site.site_address,
      client_name: site.client_name || '',
      date,
      order,
      _new: true
    };
    setSchedule(prev => ({ ...prev, [key]: [...(prev[key] || []), entry] }));
    setAddingCell(null);
    setSelectedSite('');
    setHasChanges(true);
  };

  const removeEntry = (date, workerId, entryId) => {
    const key = `${date}|${workerId}`;
    setSchedule(prev => ({
      ...prev,
      [key]: (prev[key] || []).filter(e => e.id !== entryId)
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    // Flatten all entries with proper order
    const entries = [];
    Object.entries(schedule).forEach(([key, items]) => {
      items.forEach((item, idx) => {
        entries.push({
          worker_id: item.worker_id,
          site_id: item.site_id,
          date: item.date,
          order: idx
        });
      });
    });
    try {
      const res = await fetch(`${API_URL}/api/worker/admin/schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          entries,
          start_date: weekStart,
          end_date: endDate,
          worker_ids: workers.map(w => w.id)
        })
      });
      if (res.ok) {
        setHasChanges(false);
        loadData();
      }
    } catch {}
    setSaving(false);
  };

  const formatDay = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  const isToday = (dateStr) => dateStr === new Date().toISOString().split('T')[0];

  if (loading) return (
    <div className="p-6 lg:p-8"><div className="animate-pulse space-y-4"><div className="h-8 bg-gray-200 rounded w-48"></div><div className="h-64 bg-gray-200 rounded"></div></div></div>
  );

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Weekly Schedule</h1>
          <p className="text-gray-500 text-sm">Assign blocks to workers for the week ahead</p>
        </div>
        <div className="flex items-center gap-3">
          {hasChanges && (
            <Button onClick={handleSave} disabled={saving} className="bg-[#00c853] hover:bg-[#00a844]" data-testid="save-schedule-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Schedule
            </Button>
          )}
        </div>
      </div>

      {/* Week navigation */}
      <div className="flex items-center justify-between bg-white rounded-xl border border-gray-200 p-3 mb-4">
        <button onClick={() => shiftWeek(-1)} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="prev-week-btn">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-400" />
          <span className="font-medium text-gray-900">{formatDay(weekStart)} — {formatDay(endDate)}</span>
        </div>
        <button onClick={() => shiftWeek(1)} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="next-week-btn">
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {workers.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">
          No workers — add workers from the Workers page first.
        </div>
      ) : (
        /* Schedule grid */
        <div className="overflow-x-auto">
          <div className="min-w-[800px]">
            {/* Day headers */}
            <div className="grid gap-1 mb-1" style={{ gridTemplateColumns: '140px repeat(7, 1fr)' }}>
              <div className="p-2" />
              {weekDates.map(d => (
                <div key={d} className={`p-2 text-center rounded-lg text-sm font-medium ${isToday(d) ? 'bg-[#1a1f4e] text-white' : 'bg-gray-100 text-gray-700'}`}>
                  {formatDay(d)}
                </div>
              ))}
            </div>

            {/* Worker rows */}
            {workers.map(w => (
              <div key={w.id} className="grid gap-1 mb-1" style={{ gridTemplateColumns: '140px repeat(7, 1fr)' }}>
                <div className="bg-white rounded-lg border border-gray-200 p-2 flex items-center">
                  <p className="text-sm font-medium text-gray-900 truncate">{w.name}</p>
                </div>
                {weekDates.map(d => {
                  const key = `${d}|${w.id}`;
                  const entries = schedule[key] || [];
                  const isAdding = addingCell?.date === d && addingCell?.workerId === w.id;
                  return (
                    <div key={d} className={`bg-white rounded-lg border p-2 min-h-[70px] ${isToday(d) ? 'border-[#1a1f4e]/30 bg-[#1a1f4e]/5' : 'border-gray-200'}`}>
                      {entries.map(e => (
                        <div key={e.id} data-testid={`schedule-entry-${d}-${w.id}`} className="flex items-center gap-1 bg-[#1a1f4e]/10 rounded px-2 py-1 mb-1">
                          <span className="text-xs text-gray-800 truncate flex-1">{e.site_name}</span>
                          <button onClick={() => removeEntry(d, w.id, e.id)}
                            className="text-red-400 hover:text-red-600 flex-shrink-0">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      {isAdding ? (
                        <div className="space-y-1">
                          <select
                            value={selectedSite}
                            onChange={e => setSelectedSite(e.target.value)}
                            className="w-full text-xs border border-gray-200 rounded px-1 py-1 outline-none"
                            autoFocus
                            data-testid="site-select"
                          >
                            <option value="">Select block...</option>
                            {sites.map(s => (
                              <option key={s.id} value={s.id}>{s.site_name} ({s.client_name})</option>
                            ))}
                          </select>
                          <div className="flex gap-1">
                            <button onClick={() => addToSchedule(d, w.id)} data-testid="confirm-add-block"
                              className="text-xs text-green-600 hover:text-green-800 font-medium">Add</button>
                            <button onClick={() => { setAddingCell(null); setSelectedSite(''); }}
                              className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setAddingCell({ date: d, workerId: w.id })}
                          className="w-full flex items-center justify-center gap-1 text-xs text-gray-400 hover:text-[#1a1f4e] py-1 hover:bg-gray-50 rounded transition-colors"
                          data-testid={`add-block-${d}-${w.id}`}
                        >
                          <Plus className="w-3 h-3" /> Add
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkerSchedule;
